import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Truck, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import { AssetUnitForm } from "@/components/assets/AssetUnitForm";
import { AssetGroupForm } from "@/components/assets/AssetGroupForm";
import { DispatchAssetDialog } from "@/components/order/DispatchAssetDialog";
import { useFetch } from "@/hooks/useFetch";
import { api, downloadFile, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

function RemoveRow({ u, done, cancel }) {
  const [reason, setReason] = useState("");
  const [billing, setBilling] = useState(true);
  const go = async () => {
    try { await api.post(`/assets/units/${u.id}/status`, { reason, active: false, billing_prompt: billing }); toast.success(`${u.asset_code} removed from the group`); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid={`lmo-asset-remove-form-${u.asset_code}`}>
      <Input className="h-8 w-64" placeholder="Reason (e.g. unit returned on 12 Oct)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="lmo-asset-remove-reason" />
      <label className="flex items-center gap-1"><input type="checkbox" checked={billing} onChange={(e) => setBilling(e.target.checked)} data-testid="lmo-asset-remove-billing" />Flag billing change</label>
      <Button size="sm" variant="destructive" className="h-8" disabled={reason.trim().length < 3} onClick={go} data-testid="lmo-asset-remove-save">Remove</Button>
      <Button size="sm" variant="ghost" className="h-8" onClick={cancel} data-testid="lmo-asset-remove-cancel">Cancel</Button>
    </div>
  );
}

// Link (or change) the LMO's Asset Group: pick one of the customer's groups or create a new one
function GroupLink({ o, onDone }) {
  const { can } = useMe();
  const { data, reload } = useFetch(`/assets/groups?customer_id=${o.customer_id}`);
  const [gid, setGid] = useState("");
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(!o.asset_group_id);
  const [create, setCreate] = useState(false);
  const { data: cust } = useFetch(create ? `/customers/${o.customer_id}` : null);
  if (!can("orders.edit") || ["closed", "cancelled"].includes(o.status)) return null;
  const link = async (id) => {
    try { const r = await api.post(`/orders/${o.id}/asset-group`, { asset_group_id: id, reason }); toast.success(`${r.data.asset_group_code} linked to ${o.order_no}`); setOpen(false); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!open) return <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="lmo-group-change-btn">Change Asset Group</Button>;
  const groups = (data || []).filter((g) => g.status === "active");
  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs" data-testid="lmo-group-link">
      <select className="h-8 rounded border border-slate-200 bg-white px-2 text-sm min-w-[220px]" value={gid} onChange={(e) => setGid(e.target.value)} data-testid="lmo-group-select">
        <option value="">{groups.length ? "— pick the customer's Asset Group —" : "No asset group for this customer yet"}</option>
        {groups.map((g) => <option key={g.id} value={g.id}>{g.asset_group_code}{g.site_name ? ` · ${g.site_name}` : ""}{g.name ? ` · ${g.name}` : ""}</option>)}
      </select>
      {o.asset_group_id && <Input className="h-8 w-56" placeholder="Reason for changing" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="lmo-group-reason" />}
      <Button size="sm" className="h-8" disabled={!gid} onClick={() => link(gid)} data-testid="lmo-group-link-btn">Link</Button>
      {can("assets.create") && <Button size="sm" variant="outline" className="h-8" onClick={() => setCreate(true)} data-testid="lmo-group-create-btn"><Plus className="h-4 w-4 mr-1" />New Asset Group</Button>}
      {o.asset_group_id && <Button size="sm" variant="ghost" className="h-8" onClick={() => setOpen(false)}>Cancel</Button>}
      {cust && <AssetGroupForm open={create} onOpenChange={setCreate} initialCustomer={cust} onSaved={(g) => { reload(); if (g?.id) link(g.id); }} />}
    </div>
  );
}

function PendingChallans({ o, reload }) {
  const [sp, setSp] = useSearchParams();
  const { data, reload: again } = useFetch(`/assets/from-dispatch?order_id=${o.id}`);
  const [open, setOpen] = useState(sp.get("dispatch"));
  const close = () => { setOpen(null); if (sp.get("dispatch")) { sp.delete("dispatch"); setSp(sp, { replace: true }); } };
  return (
    <>
      {data?.length > 0 && (
        <div className="mb-3 rounded border border-sky-200 bg-sky-50 p-2 text-xs" data-testid="lmo-pending-challans">
          <div className="mb-1 font-semibold text-sky-900">Equipment sent on delivery challan - not yet added as assets</div>
          {data.map((d) => (
            <div key={d.id} className="flex items-center gap-2 py-0.5"><Truck className="h-3.5 w-3.5 text-sky-700" /><span className="font-mono">{d.dispatch_no}</span><span>DC {d.dc_no || "—"} · {fmtDate(d.dispatch_date)} · {d.items} line(s)</span>
              <Button size="sm" variant="outline" className="h-7 ml-auto" onClick={() => setOpen(d.id)} data-testid={`lmo-challan-assets-${d.dispatch_no}`}>Add as assets</Button></div>
          ))}
        </div>
      )}
      {open && <DispatchAssetDialog dispatchId={open} onClose={close} onDone={() => { again(); reload(); }} />}
    </>
  );
}

// LMO > Assets: upload serials (Asset Unit template) - rows default to Preloaded unless origin_mode = historical
function SerialUpload({ o, onDone }) {
  const ref = useRef(null);
  const [res, setRes] = useState(null);
  const [file, setFile] = useState(null);
  const send = async (f, commit) => {
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post(`/assets/import/units?commit=${commit}&lmo_id=${o.id}`, fd);
      setRes(r.data);
      if (commit) { toast.success("Serials uploaded"); setFile(null); onDone(); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const bad = (res?.rows || []).filter((r) => r.status === "error");
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => ref.current?.click()} data-testid="lmo-serial-upload-btn"><Upload className="h-4 w-4 mr-1" />Upload serials</Button>
      <Button size="sm" variant="ghost" onClick={() => downloadFile("/assets/import/units/template", "asset_unit_template.xlsx").catch((e) => toast.error(errMsg(e)))} data-testid="lmo-serial-template-btn">Template</Button>
      <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" data-testid="lmo-serial-upload-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { setFile(f); send(f, false); } }} />
      {res && !res.committed && (
        <div className="w-full rounded border border-slate-200 bg-slate-50 p-2 text-xs" data-testid="lmo-serial-preview">
          <div>{res.rows.length} row(s): {res.rows.length - bad.length} ok, {bad.length} with errors. Rows become <b>Preloaded</b> (Active once their challan is delivered) unless origin_mode is historical.</div>
          {bad.slice(0, 8).map((r) => <div key={r.row} className="text-red-700">Row {r.row}: {r.message}</div>)}
          <div className="mt-1 flex gap-2"><Button size="sm" className="h-7" disabled={!file || bad.length === res.rows.length} onClick={() => send(file, true)} data-testid="lmo-serial-commit">Upload {res.rows.length - bad.length} row(s)</Button>
            <Button size="sm" variant="ghost" className="h-7" onClick={() => setRes(null)}>Close</Button></div>
        </div>
      )}
    </>
  );
}

// LMO > Assets: add or remove units in the contract's Asset Group; billing prompt is optional
export function LmoAssetsTab({ o, reloadOrder }) {
  const { can } = useMe();
  const { data: g, reload } = useFetch(o.asset_group_id ? `/assets/groups/${o.asset_group_id}` : null);
  const [add, setAdd] = useState(false);
  const [rm, setRm] = useState(null);
  if (!o.asset_group_id) return (
    <div className="space-y-2" data-testid="lmo-assets-no-group">
      <p className="text-sm text-amber-700">Link an Asset Group (the customer site) to this LMO to manage its assets.{["Rental", "Operate", "Rental & Operate"].includes(o.contract_type) ? " Rental / Operate LMOs need one before submitting." : ""}</p>
      <GroupLink o={o} onDone={reloadOrder} />
    </div>
  );
  if (!g) return null;
  const edit = can("assets.create") && g.status === "active";
  const units = (g.units || []).filter((u) => u.active);
  const live = units.filter((u) => ["active", "installed"].includes(u.lifecycle_status)).length;
  return (
    <div data-testid="lmo-assets-tab">
      <PendingChallans o={o} reload={reload} />
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-xs text-slate-600">Asset Group <Link className="font-mono text-sky-700" to={`/assets/groups/${g.id}`}>{g.asset_group_code}</Link> · {units.length} unit(s): {live} active / installed, {units.length - live} preloaded or awaiting delivery. Adding or removing a unit can flag the matching billing period for review (Billing schedule tab).</span>
        {edit && <Button size="sm" onClick={() => setAdd(true)} data-testid="lmo-asset-add-btn"><Plus className="h-4 w-4 mr-1" />Add asset</Button>}
        {edit && <SerialUpload o={o} onDone={reload} />}
        <GroupLink o={o} onDone={reloadOrder} />
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="th text-left">{["Asset", "Item", "Type", "Serial", "Commissioned", "Life stage", "Status", ""].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>{units.map((u) => (
          <tr key={u.id} className="border-b border-slate-100 align-top" data-testid={`lmo-asset-row-${u.asset_code}`}>
            <td className="px-3 py-2 font-mono whitespace-nowrap"><Link className="text-sky-700" to={`/assets/units/${u.id}`}>{u.asset_code}</Link></td>
            <td className="px-3 py-2 text-xs">{u.unit_name}<div className="font-mono text-slate-500">{u.item_code}</div></td>
            <td className="px-3 py-2 text-xs">{u.asset_type || "—"}</td><td className="px-3 py-2 font-mono text-xs">{u.serial_no || "—"}</td>
            <td className="px-3 py-2 text-xs">{fmtDate(u.commissioning_date) || "—"}</td>
            <td className="px-3 py-2" data-testid={`lmo-asset-lifecycle-${u.asset_code}`}><StatusBadge status={u.lifecycle_status} /><div className="text-[10px] text-slate-500">{u.addition_label}</div></td><td className="px-3 py-2"><StatusBadge status={u.unit_status} /></td>
            <td className="px-3 py-2">{edit && (rm === u.id ? <RemoveRow u={u} cancel={() => setRm(null)} done={() => { setRm(null); reload(); }} /> : <Button size="sm" variant="outline" className="h-7" onClick={() => setRm(u.id)} data-testid={`lmo-asset-remove-${u.asset_code}`}>Remove</Button>)}</td>
          </tr>))}
          {units.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No active assets in this group</td></tr>}
        </tbody></table></div>
      <AssetUnitForm open={add} onOpenChange={setAdd} group={g} billingToggle lmo={o} onSaved={reload} />
    </div>
  );
}
